import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { institutionsMarketingVisible } from "../../lib/featureFlags";
import { Button } from "../ui/button";
import { Med900Logo } from "./Med900Logo";
import { Reveal } from "./hooks";
import { ArrowRight } from "lucide-react";

const NAV_LINK_CLASS = ({ isActive }) =>
  `hover:text-foreground transition-colors ${isActive ? "text-foreground" : ""}`;

const FOOTER_COLUMNS = [
  {
    title: "Practice",
    links: [
      { text: "Platform", to: "/platform" },
      { text: "Why Med900", to: "/why-med900" },
      { text: "Study Guides", to: "/guides" },
    ],
  },
  {
    title: "Resources",
    links: [
      { text: "UCAT Guides", to: "/guides" },
      { text: "Verbal Reasoning", to: "/ucat/verbal-reasoning" },
      { text: "Decision Making", to: "/ucat/decision-making" },
      { text: "FAQ", to: "/faq" },
    ],
  },
  {
    title: "Company",
    links: [
      { text: "About", to: "/about" },
      { text: "Pricing", to: "/pricing" },
      { text: "Contact", to: "/contact" },
    ],
  },
  {
    title: "Legal",
    links: [
      { text: "Privacy Policy", to: "/privacy" },
      { text: "Terms of Service", to: "/terms" },
    ],
  },
];

function MarketingFooter() {
  return (
    <footer
      data-testid="landing-footer"
      style={{
        background:
          "linear-gradient(180deg, hsl(0 0% 100%) 0%, hsl(220 30% 94%) 50%, hsl(225 35% 88%) 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto pt-16 pb-12 px-4">
        <Reveal>
          <div className="mb-14">
            <Med900Logo size="text-xl" className="mb-4" />
            <h2 className="text-3xl sm:text-4xl font-cabinet font-black tracking-tight text-foreground/40 leading-tight">
              Data-driven preparation
              <br />
              for the UCAT.
            </h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-14">
          {FOOTER_COLUMNS.map((col) => (
            <div key={col.title}>
              <div className="text-sm font-medium text-muted-foreground mb-4 font-plex">
                {col.title}
              </div>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.text}>
                    {l.external ? (
                      <a
                        href={l.to}
                        className="text-sm text-foreground/70 hover:text-foreground transition-colors font-plex"
                      >
                        {l.text}
                      </a>
                    ) : (
                      <Link
                        to={l.to}
                        className="text-sm text-foreground/70 hover:text-foreground transition-colors font-plex"
                      >
                        {l.text}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-foreground/10 pt-6">
          <p className="text-sm text-muted-foreground font-plex">
            &copy; Med900 Ltd 2024&ndash;2026
          </p>
        </div>
      </div>
    </footer>
  );
}

const MARKETING_NAV_FALLBACK = "5.75rem"; // promo banner + h-14 nav row

export function MarketingLayout({ children, solidNav = false }) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const navRef = useRef(null);
  const [navHeight, setNavHeight] = useState(null);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;

    const updateHeight = () => setNavHeight(el.offsetHeight);
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const navOpaque = solidNav || scrolled;

  return (
    <div className="min-h-screen bg-background">
      <nav
        ref={navRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navOpaque ? "bg-background/95 backdrop-blur-md border-b border-border shadow-sm" : "bg-transparent"}`}
        data-testid="landing-navbar"
      >
        <div className="bg-[#0A0A0A] text-white text-sm text-center py-2 px-4 font-medium font-plex">
          20,000 active UCAT questions and 25 full mocks now live &mdash;{" "}
          <Link
            to="/signup"
            className="underline font-semibold hover:opacity-80"
          >
            start free
          </Link>
        </div>
        <div className="relative flex items-center h-14 px-4 lg:px-6 max-w-[1440px] mx-auto">
          <div className="flex flex-1 items-center">
            <Med900Logo size="text-xl" asLink />
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm font-medium text-muted-foreground font-plex absolute left-1/2 -translate-x-1/2">
            <NavLink to="/platform" className={NAV_LINK_CLASS}>
              Platform
            </NavLink>
            <NavLink to="/why-med900" className={NAV_LINK_CLASS}>
              Why Med900
            </NavLink>
            <NavLink to="/pricing" className={NAV_LINK_CLASS}>
              Pricing
            </NavLink>
            <NavLink to="/guides" className={NAV_LINK_CLASS}>
              Study Guides
            </NavLink>
            {institutionsMarketingVisible() && (
              <Link
                to="/institutions"
                className="hover:text-foreground transition-colors"
              >
                For Institutions
              </Link>
            )}
          </div>
          <div className="flex flex-1 items-center justify-end gap-3">
            {isAuthenticated ? (
              <Button
                onClick={() => navigate("/dashboard")}
                data-testid="go-to-dashboard"
                className="rounded-full font-cabinet"
              >
                Dashboard <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <>
                <Link to="/login">
                  <Button
                    variant="ghost"
                    data-testid="login-btn"
                    className="text-sm font-plex"
                  >
                    Log in
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button
                    data-testid="signup-btn"
                    size="sm"
                    className="rounded-full font-cabinet"
                  >
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      <div
        aria-hidden="true"
        className="pointer-events-none shrink-0"
        style={{ height: navHeight ?? MARKETING_NAV_FALLBACK }}
      />

      <main>{children}</main>

      <MarketingFooter />
    </div>
  );
}
